/**
 * Update už naimportovanej zákazky z opraveného exportu (KOLO 118).
 *
 * User nájde chybu v modeli až keď je zákazka v appke, opraví ju
 * v TopSolide a exportuje znova. Dovtedy sa dal spraviť len nový import
 * alebo ďalšia výrobná varianta — teda kópia vedľa, nie update.
 *
 * Náhľad **nič nezapisuje** (backend transakciu odroluje), takže sa dá
 * pozrieť aj opakovane. Undo je jeden krok za celú operáciu a stojí na
 * tom, čo vrátil server — undo v prehliadači je len 20 krokov v pamäti a maže
 * sa pri zmene zákazky, takže je to poistka, nie jediná cesta späť.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import { pridajVyber } from "@/lib/import/vyberZostav";
import { useUndoOptional } from "@/lib/undo/UndoContext";
import type { DataResponse } from "@/api/schemas";

/** Stavy sú tie isté ako pri „Porovnať výpočty" — jeden slovník. */
export type StavZmeny = "novy" | "zmeneny" | "zmizol" | "rovnaky";

export interface ZmenaPola {
  pole: string;
  zo: string | null;
  na: string | null;
  /** Zmenil to user **aj** TopSolid — bez odklikania sa nechá. */
  konflikt: boolean;
}

export interface DielecZmena {
  stav: StavZmeny;
  kod: string;
  nazov: string;
  zostava_kod: string;
  dielec_id: string | null;
  zmeny: ZmenaPola[];
  konflikt: boolean;
}

/**
 * Kovanie zostavy — export vs. zákazka (KOLO 220). Páruje sa po zostave
 * a materiáli (sety ako v kusovníku), nie po riadku exportu.
 */
export interface KovanieZmena {
  stav: StavZmeny;
  zostava_kod: string;
  zostava_nazov: string;
  nazov: string;
  mj: string;
  material_id: string;
  pocet_zo: number | null;
  pocet_na: number | null;
  /** Riadok v zákazke — Zmazať (zmizne), Prepísať (sporný počet) */
  dielec_id: string | null;
  /** Pribudne — kľúč do `nepridat` */
  kluc: string | null;
  /** Predvolená fajka: pridať / zmazať / prepísať */
  predvolene: boolean;
  /** Pridal ho user — predvolene ostáva */
  rucne: boolean;
  konflikt: boolean;
}

/** Voľby kovania z okna — čo zmazať a čo nepridať (KOLO 220). */
export interface KovanieVolby {
  zmazat: string[];
  nepridat: string[];
}

export interface UpdateNahlad {
  novych: number;
  zmenenych: number;
  zmiznutych: number;
  konfliktov: number;
  dielce: DielecZmena[];
  nove_zostavy: string[];
  zmiznute_zostavy: string[];
  /** Názov = jmeno, Popis = urceni z Tandem Zostáv (KOLO 146) */
  premenovane_zostavy?: ZostavaPremenovanie[];
  /** Poistka (KOLO 203): koľko dielcov výpočet mal a či zmizne aspoň
   *  polovica — export je skôr iná časť zákazky než oprava. */
  povodnych?: number;
  zmizne_vacsina?: boolean;
  /** Čo sa aktualizuje — „výpočet" / „výroba" / „časť" (KOLO 218). */
  co?: string;
  /** Kovanie po zostavách, len zmeny (KOLO 220) */
  kovanie?: KovanieZmena[];
  /** Zostavy exportu, ktoré sú v zákazke viackrát — kým ich user
   *  nezlúči, server aktualizáciu nepustí (KOLO 221) */
  dvojite_zostavy?: { urceni: string; kody: string[] }[];
}

export interface ZostavaPremenovanie {
  zostava_id: string;
  kod: string;
  nazov_zo: string;
  nazov_na: string;
  popis_zo?: string | null;
  popis_na?: string | null;
}

export interface VratenieDielca {
  dielec_id: string;
  polia: Record<string, string | null>;
}

export interface Vratenie {
  zmenene: VratenieDielca[];
  pridane: string[];
  zmazane: string[];
  zostavy?: { zostava_id: string; nazov: string; popis?: string | null }[];
}

export interface UpdateVysledok {
  zmenenych: number;
  pridanych: number;
  zmazanych: number;
  premenovanych?: number;
  vratenie: Vratenie;
}

/** Spoločný multipart — náhľad aj vykonanie posielajú to isté. */
function telo(vstup: {
  file: File;
  rozhodnutia: unknown;
  delenie?: unknown;
  zaradenie?: unknown;
  materialy?: unknown;
  pracovneDosky?: unknown;
  zostavyMena?: Record<string, string> | null;
  zostavyVybrane?: string[] | null;
  prepisat?: string[];
  kovanie?: KovanieVolby;
}): FormData {
  const fd = new FormData();
  fd.append("file", vstup.file);
  fd.append("rozhodnutia_json", JSON.stringify(vstup.rozhodnutia));
  for (const [kluc, hodnota] of [
    ["delenie_json", vstup.delenie],
    ["zaradenie_json", vstup.zaradenie],
    ["materialy_json", vstup.materialy],
    // „Je to pracovná / plošná doska" z okna (KOLO 168)
    ["pracovne_dosky_json", vstup.pracovneDosky],
    ["zostavy_mena_json", vstup.zostavyMena],
  ] as const) {
    if (hodnota && Object.keys(hodnota).length > 0) {
      fd.append(kluc, JSON.stringify(hodnota));
    }
  }
  // Náhľad aj zápis len v rámci vybraných zostáv (KOLO 175)
  pridajVyber(fd, vstup.zostavyVybrane);
  if (vstup.prepisat && vstup.prepisat.length > 0) {
    fd.append("prepisat_json", JSON.stringify(vstup.prepisat));
  }
  // Aj prázdne voľby — bez poľa server kovanie nechá (okno spred KOLA 220)
  if (vstup.kovanie) {
    fd.append("kovanie_json", JSON.stringify(vstup.kovanie));
  }
  return fd;
}

export interface UpdateVstup {
  file: File;
  rozhodnutia: unknown;
  delenie?: unknown;
  zaradenie?: unknown;
  materialy?: unknown;
  pracovneDosky?: unknown;
  zostavyMena?: Record<string, string> | null;
  /** Len vybrané zostavy z Tandem Zákazky (KOLO 175) */
  zostavyVybrane?: string[] | null;
}

export function useUpdateNahlad(
  variantaId: string,
): UseMutationResult<UpdateNahlad, Error, UpdateVstup> {
  return useMutation({
    mutationFn: async (vstup) => {
      const { data } = await api.post<DataResponse<UpdateNahlad>>(
        `/import/topsolid/update/${variantaId}/nahlad`,
        telo(vstup),
      );
      return data.data;
    },
  });
}

export function useUpdateVykonat(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<
  UpdateVysledok,
  Error,
  UpdateVstup & { prepisat: string[]; kovanie?: KovanieVolby }
> {
  const qc = useQueryClient();
  const undo = useUndoOptional();

  return useMutation({
    mutationFn: async (vstup) => {
      const { data } = await api.post<DataResponse<UpdateVysledok>>(
        `/import/topsolid/update/${variantaId}`,
        telo(vstup),
      );
      return data.data;
    },
    onSuccess: (vysledok) => {
      void qc.invalidateQueries({
        queryKey: queryKeys.zakazky.detail(zakazkaId),
      });
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
      const { vratenie } = vysledok;
      if (
        vratenie.zmenene.length === 0 &&
        vratenie.pridane.length === 0 &&
        vratenie.zmazane.length === 0
      ) {
        return;
      }
      undo?.pridaj({
        popis: "aktualizáciu z opraveného exportu",
        vrat: async () => {
          await api.post(
            `/import/topsolid/update/${variantaId}/vratit`,
            vratenie,
          );
          await qc.invalidateQueries({
            queryKey: queryKeys.zakazky.detail(zakazkaId),
          });
        },
      });
    },
  });
}
